import { insertAtCursor } from "@/components/mentor-workflows/attention/attention-composer-insert";
import { useSendTrainingMessage } from "@/lib/api/training-simulator/training-simulator";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events.ts";
import { GifIcon } from "@heroicons/react/24/outline";
import {
  Composer,
  EmojiPicker,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@somethings-ui/web";
import { Camera, Smile } from "lucide-react";
import { useCallback, useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";

interface TrainingMessageComposerProps {
  attemptId: string;
  threadId: string;
  disabled?: boolean;
  onSend?: () => void;
}

export function TrainingMessageComposer({
  attemptId,
  threadId,
  disabled = false,
  onSend,
}: TrainingMessageComposerProps) {
  const [value, setValue] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { track } = useTrackEvent();
  const { mutate: sendMessage, isPending } = useSendTrainingMessage(
    attemptId,
    threadId,
  );

  const trimmed = value.trim();
  const canSend = trimmed.length > 0 && !disabled && !isPending;

  const resize = useCallback((element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    track(PostHogEvents.SIMULATOR_MESSAGE_SENT, {
      attempt_id: attemptId,
      thread_id: threadId,
      char_count: trimmed.length,
    });
    sendMessage({ content: trimmed });
    setValue("");
    onSend?.();
    requestAnimationFrame(() => {
      const element = inputRef.current;
      if (!element) return;
      element.style.height = "auto";
    });
  }, [attemptId, canSend, onSend, sendMessage, threadId, track, trimmed]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setValue(event.target.value);
      resize(event.currentTarget);
    },
    [resize],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      const element = inputRef.current;
      const start = element?.selectionStart ?? value.length;
      const end = element?.selectionEnd ?? value.length;
      const { next, caret } = insertAtCursor(value, emoji, start, end);
      setValue(next);
      setEmojiOpen(false);
      requestAnimationFrame(() => {
        if (!element) return;
        element.focus();
        element.setSelectionRange(caret, caret);
        resize(element);
      });
    },
    [resize, value],
  );

  return (
    <div className="shrink-0 p-4">
      <Composer>
        <Composer.Card>
          <Composer.Input
            ref={inputRef}
            rows={1}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Your message..."
            disabled={disabled}
            aria-label="Message"
            className="subtle-scrollbar max-h-40 overflow-y-auto"
          />
          <Composer.Toolbar>
            <Composer.ToolbarGroup>
              <Composer.VoiceToolbarButton
                status="idle"
                disabled={disabled}
                onStart={() => undefined}
                onPause={() => undefined}
                onResume={() => undefined}
              />
              <Composer.ToolbarButton
                aria-label="Add photo or video"
                disabled={disabled}
                icon={<Camera className="size-6" />}
              />
              <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                <PopoverTrigger asChild>
                  <Composer.ToolbarButton
                    aria-label="Add emoji"
                    active={emojiOpen}
                    disabled={disabled}
                    icon={<Smile className="size-6" />}
                  />
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-fit border-0 bg-transparent p-0 shadow-none"
                >
                  <EmojiPicker onEmojiSelect={handleEmojiSelect} />
                </PopoverContent>
              </Popover>
              <Composer.ToolbarButton
                aria-label="Add GIF"
                disabled={disabled}
                icon={<GifIcon className="size-6" />}
              />
            </Composer.ToolbarGroup>
            <Composer.SendButton disabled={!canSend} onClick={handleSend} />
          </Composer.Toolbar>
        </Composer.Card>
      </Composer>
    </div>
  );
}
